/**
 * NewDevicesTable - Display unmatched devices from Starlink/Sol-Ark
 * Shows devices that don't have matching unit entries in the database
 */

import { useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { Sun, Wifi, MapPin, Plus, Link2, X, Check, AlertTriangle, Inbox, MoreHorizontal, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { NewDevice } from '@/hooks/useNewDevices';

interface NewDevicesTableProps {
  devices: NewDevice[];
  loading?: boolean;
  onCreateUnit: (device: NewDevice) => void;
  onLinkToUnit: (device: NewDevice) => Promise<void>;
  onDismiss: (device: NewDevice) => Promise<void>;
  onApproveAll?: () => void | Promise<void>;
  approvingAll?: boolean;
  pendingCount?: number;
}

/**
 * Source icon component
 */
function SourceIcon({ source }: { source: string }) {
  if (source === 'solark') {
    return (
      <span className="inline-flex items-center gap-1.5 text-amber-400">
        <Sun className="w-4 h-4" />
        <span className="text-xs">Sol-Ark</span>
      </span>
    );
  }
  if (source === 'starlink') {
    return (
      <span className="inline-flex items-center gap-1.5 text-sky-400">
        <Wifi className="w-4 h-4" />
        <span className="text-xs">Starlink</span>
      </span>
    );
  }
  if (source === 'omni') {
    return (
      <span className="inline-flex items-center gap-1.5 text-purple-400">
        <MapPin className="w-4 h-4" />
        <span className="text-xs">GPS</span>
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">{source}</span>;
}

/**
 * Last comms status indicator
 */
function LastCommsStatus({ timestamp }: { timestamp: string | null }) {
  if (!timestamp) {
    return <span className="text-muted-foreground/50">—</span>;
  }

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = diffMs / (1000 * 60);
  const diffHours = diffMinutes / 60;

  let indicator = null;
  let colorClass = 'text-muted-foreground';

  if (diffMinutes < 15) {
    // Recent - green checkmark
    indicator = <Check className="w-3 h-3 text-[#8FB560]" />;
    colorClass = 'text-foreground';
  } else if (diffHours > 1) {
    // Stale - amber warning
    indicator = <AlertTriangle className="w-3 h-3 text-amber-400" />;
    colorClass = 'text-amber-400';
  }

  return (
    <span className={`inline-flex items-center gap-1 ${colorClass}`}>
      <span title={format(date, 'MMM d, yyyy h:mm a')}>
        {formatDistanceToNow(date, { addSuffix: false })}
      </span>
      {indicator}
    </span>
  );
}

/**
 * Format first seen date
 */
function formatFirstSeen(timestamp: string | null): string {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  const now = new Date();
  const sameYear = date.getFullYear() === now.getFullYear();

  if (sameYear) {
    return format(date, 'MMM d');
  }
  return format(date, 'MMM yyyy');
}

export function NewDevicesTable({
  devices,
  loading = false,
  onCreateUnit,
  onLinkToUnit,
  onDismiss,
  onApproveAll,
  approvingAll = false,
  pendingCount = devices.length,
}: NewDevicesTableProps) {
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleLinkClick = async (device: NewDevice) => {
    setActionLoading(device.id);
    try {
      await onLinkToUnit(device);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismissClick = async (device: NewDevice) => {
    setActionLoading(device.id);
    try {
      await onDismiss(device);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading new devices...</div>
      </div>
    );
  }

  if (devices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 border border-border rounded-lg bg-card/50">
        <Inbox className="w-12 h-12 text-muted-foreground/50 mb-4" />
        <div className="text-muted-foreground text-sm">No unmatched devices found</div>
        <p className="text-xs text-muted-foreground/60 mt-1">
          Devices that don't match any units will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <span className="text-sm text-amber-200">
            {pendingCount} device{pendingCount !== 1 ? 's' : ''} found without matching units.
            {onApproveAll
              ? ' Review their source names and approve safe matches in bulk.'
              : ' Review each source name before creating or linking its unit.'}
          </span>
        </div>
        {onApproveAll && (
          <Button
            type="button"
            size="sm"
            onClick={onApproveAll}
            disabled={approvingAll || pendingCount === 0}
            className="shrink-0 bg-[#8FB560] text-black hover:bg-[#8FB560]/90"
          >
            {approvingAll ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            Review &amp; Approve All
          </Button>
        )}
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-card border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Device Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  First Seen
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Last Comms
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Seen
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {devices.map((device) => {
                const isLoading = actionLoading === device.id;

                return (
                  <tr
                    key={device.id}
                    className="hover:bg-muted/50 transition-colors"
                  >
                    {/* Device Name */}
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-foreground">
                          {device.external_name || device.external_id}
                        </span>
                        {device.external_name && (
                          <span className="text-xs text-muted-foreground font-mono mt-0.5">
                            {device.source === 'solark' ? 'Plant ID' : 'External ID'}: {device.external_id}
                          </span>
                        )}
                        {device.suggested_unit_code && device.suggested_unit_code !== device.external_name && (
                          <span className="text-xs text-muted-foreground italic mt-0.5">
                            Suggested: {device.suggested_unit_code}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Source */}
                    <td className="px-4 py-3">
                      <SourceIcon source={device.source} />
                    </td>

                    {/* First Seen */}
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatFirstSeen(device.first_seen_at)}
                    </td>

                    {/* Last Comms */}
                    <td className="px-4 py-3 text-sm">
                      <LastCommsStatus timestamp={device.last_comms_at} />
                    </td>

                    {/* Seen Count */}
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {device.seen_count}x
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Primary action: Create Unit */}
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => onCreateUnit(device)}
                          disabled={isLoading}
                          className="h-7 text-xs bg-[#8FB560] hover:bg-[#8FB560]/90 text-black"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Create Unit
                        </Button>

                        {/* Secondary actions in dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              disabled={isLoading}
                            >
                              {isLoading ? (
                                <div className="w-3 h-3 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                              ) : (
                                <MoreHorizontal className="w-4 h-4" />
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-card border-border">
                            <DropdownMenuItem
                              onClick={() => handleLinkClick(device)}
                              className="cursor-pointer"
                            >
                              <Link2 className="w-4 h-4 mr-2" />
                              Link to Existing Unit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDismissClick(device)}
                              className="cursor-pointer text-muted-foreground"
                            >
                              <X className="w-4 h-4 mr-2" />
                              Dismiss
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer info */}
      <div className="text-xs text-muted-foreground px-2">
        {pendingCount} pending device{pendingCount !== 1 ? 's' : ''}
      </div>
    </div>
  );
}

export default NewDevicesTable;
